import { createContext, useContext } from 'react'
import { Theme } from '@mui/material/styles'

import { DRAWER_WIDTH_CLOSED_PX } from '@/common/constants'

interface NavDrawerState {
  open: boolean
  /**
   * Left offset the page content is currently held at, in pixels. This is not
   * always the drawer's rendered width — see `CurbsNav` for why it lags on the
   * way closed.
   */
  contentOffset: number
}

// Defaults to the collapsed rail: the geographies overlay also renders under the
// legacy mobility layout, which has no drawer and so no provider.
export const NavDrawerContext = createContext<NavDrawerState>({
  open: false,
  contentOffset: DRAWER_WIDTH_CLOSED_PX,
})

export const useNavDrawer = (): NavDrawerState => useContext(NavDrawerContext)

/**
 * Gutter between the nav rail and the chrome that floats beside it, in pixels.
 * Keeps overlays reading as floating on the map rather than hanging off the drawer.
 */
export const NAV_GUTTER_PX = 16

/**
 * The drawer's width animation, shared by the chrome that tracks its edge.
 *
 * Deliberately not used for the page content: a transition on the content's
 * margin resizes every ResizeObserver inside it once a frame, and MUI's charts
 * answer each one with a forced sync layout plus a full re-render of the SVG.
 */
export const navDrawerTransition = (
  theme: Theme,
  open: boolean,
  properties: string | string[]
): string =>
  theme.transitions.create(properties, {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  })
